import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import { InformationIcon } from "~/components/ui/icons/information";
import { toast } from "~/components/ui/toast";
import type { OrderQuery } from "~/graphql/graphql-new.gen";
import {
  useAcceptOrderClaimWithRefundMutation,
  useOrderQuery,
} from "~/graphql/graphql-new.gen";
import { StoreRegion } from "~/graphql/graphql.gen";
import { brandState } from "~/stores/brand";
import { activeSaleState } from "~/stores/sales";
import { transformPrice } from "~/utils/currency";

type OrderConfirmRefundProps = {
  claim: OrderQuery["order"]["claims"][0];
  order?: OrderQuery["order"];
  orderId?: OrderQuery["order"]["id"];
  setIsOpen: (isOpen: boolean) => void;
};

export const ModalConfirmRefund: React.FC<OrderConfirmRefundProps> = ({
  claim,
  order,
  orderId,
  setIsOpen,
}: OrderConfirmRefundProps) => {
  const queryClient = useQueryClient();
  const { i18n, t } = useTranslation();

  const activeSale = useAtomValue(activeSaleState);
  const brand = useAtomValue(brandState);

  const {
    mutateAsync: acceptClaimWithRefundMutation,
    isPending,
    error,
  } = useAcceptOrderClaimWithRefundMutation();

  useEffect(() => {
    if (error) {
      toast.error(t("order.modals.modal_refund.refund_error"));
    }
  }, [error]);

  const getRefundPrice = () => {
    const claimedProducts = claim.items;
    if (claimedProducts.length > 0) {
      const total_amount = claimedProducts
        .map((product) => product.priceSold.valueWithVat)
        .reduce((prev, curr) => prev + curr, 0);
      return transformPrice(
        total_amount,
        i18n.language === "fr" ? "fr" : "en",
        activeSale?.currency.toString().toUpperCase() ?? "EUR",
      );
    }
    return transformPrice(
      brand?.store === StoreRegion.Fr
        ? order?.totalPriceSold.valueWithVat
        : (order?.totalPriceSold.valueWithoutVat ?? 0),
      i18n.language === "fr" ? "fr" : "en",
      activeSale?.currency.toString().toUpperCase() ?? "EUR",
    );
  };

  const getRefundPriceFdp = () => {
    const fdp = transformPrice(
      order?.shipping.feesSold ?? 0,
      i18n.language === "fr" ? "fr" : "en",
      activeSale?.currency.toString().toUpperCase() ?? "EUR",
    );

    const claimedProducts = claim.items;
    if (claimedProducts.length > 0) {
      return claimedProducts.length === order?.items.length
        ? t("order.amount_shipping_fees", { amount: fdp })
        : t("order.amount_shipping_fees", {
            amount: transformPrice(
              0,
              i18n.language === "fr" ? "fr" : "en",
              activeSale?.currency.toString().toUpperCase() ?? "EUR",
            ),
          });
    }
    return t("order.amount_shipping_fees", { amount: fdp });
  };

  const onConfirm = async () => {
    await acceptClaimWithRefundMutation({
      input: {
        orderId: orderId ?? "",
        claimId: claim.id,
      },
    });

    queryClient.invalidateQueries({
      queryKey: useOrderQuery.getKey({ orderId: orderId ?? "" }),
    });
    queryClient.invalidateQueries({
      queryKey: ["orders"],
    });
    setIsOpen(false);
    toast.success(t("order.modals.modal_refund.claim_processed"));
  };

  return (
    <div className="flex flex-col overflow-hidden">
      <div className="overflow-y-auto p-6 md:p-10">
        <h3
          id="modal-refund-title"
          className="mb-4 text-2xl font-bold text-gray-900"
        >
          {t("order.actions_refund_product", { count: claim.items.length })}
        </h3>
        <p className="text-sm text-gray-700">
          {t("order.modals.modal_refund.confirm_refund")}{" "}
          <span className="font-semibold">
            {getRefundPrice()} ({getRefundPriceFdp()})
          </span>
        </p>
        <div className="mb-10 mt-8 flex items-center gap-4 rounded border border-gray-300 bg-[#FBFBFB] p-6 text-gray-900">
          <InformationIcon className="size-6" />
          <span className="text-sm font-semibold">
            {t("order.modals.modal_refund.confirm_refund_info")}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 justify-end space-x-6 border-t border-gray-100 p-6 md:p-10 md:pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsOpen(false)}
        >
          {t("cancel")}
        </Button>{" "}
        <Button type="button" onClick={() => onConfirm()} disabled={isPending}>
          {t("confirm")}
        </Button>
      </div>
    </div>
  );
};
